// Initialize window.Woolman object
if (typeof window.Woolman == 'undefined') {
  window.Woolman = {};
}

/* ---------------------------------------
 * Backorder line item property sync
 *
 * The backorder property is written once, when a line is added to the cart, and
 * is never revisited. A line therefore goes stale when its variant is restocked
 * (or sells out) while the cart sits around for a few days: ops would be emailed
 * about a backorder that can ship immediately, and Klaviyo snapshots whatever
 * the line carries at orders/create.
 *
 * Nothing after the order exists can correct it, so the property has to be
 * rewritten while it is still a cart line. Every checkout entry point the theme
 * can reach is deferred until the cart is in sync:
 * - the cart page and native drawer, reachable by direct URL and by the "Return
 *   to cart" link in checkout
 * - the add-to-cart notification, which posts a checkout form of its own
 * - the Rebuy Smart Cart drawer, which renders its own checkout button
 *
 * Accelerated checkout (Shop Pay, Apple Pay, Google Pay) submits straight to
 * Shopify and cannot be deferred, so those buttons are inert until an eager sync
 * has resolved: on the cart page at load, and whenever the native or Rebuy
 * drawer opens.
 *
 * Each backorder line carries the same lead time under two keys:
 * - the translated label of the active locale, which is the customer-facing one
 *   Shopify prints on the cart, in checkout, on the order status page and in
 *   admin. A line added before a language switch is keyed in the previous
 *   locale, so every locale's label is treated as the backorder property on read
 *   and stripped on write, which also re-keys the line to the current language.
 * - `_backorder_lead_time`, private and never shown, so Flow and Klaviyo match
 *   one fixed key instead of every locale's label. It is the automation contract
 *   and must not be renamed without updating both.
 ----------------------------------------*/

Woolman.BackorderCartSync = {
  sectionId: 'cart-backorder-status',
  // The private mirror of the lead time. Also does duty as the marker on a line
  // that has to stay in the cart without a visible backorder property, since
  // /cart/change.js ignores an empty properties object.
  privateKey: '_backorder_lead_time',
  checkoutButtonSelector:
    'form#cart button[name="checkout"], form#cart-notification-form button[name="checkout"], .rebuy-cart__checkout-button',
  walletSelector: '.additional-checkout-buttons, [data-rebuy-cart-additional-checkout-buttons]',
  inFlight: null,
  pendingCheckoutButton: null,
  bypassButton: null,
  rebuyObserver: null,
  rebuyDrawer: null,
  nativeDrawerObserver: null,

  /**
   * The label to write, in the language the buyer is currently browsing.
   * @returns {String} The backorder line item property key for this locale
   */
  get propertyKey() {
    const strings = (window.variantStrings && window.variantStrings.inventory) || {};

    return strings.backorder_property || '';
  },

  /**
   * Every locale's label, so a line added in another language is still
   * recognised as the backorder property rather than left behind as a duplicate.
   * @returns {Array} The backorder line item property keys
   */
  get knownPropertyKeys() {
    const strings = (window.variantStrings && window.variantStrings.inventory) || {};

    return strings.backorder_property_keys || [this.propertyKey];
  },

  init() {
    this.bindCheckoutButtons();
    this.bindNativeDrawer();
    this.bindRebuyDrawer();

    if (document.documentElement.classList.contains('template-cart')) {
      this.syncBeforeWalletCheckout();
    }
  },

  /**
   * Runs the sync once no matter how many checkout affordances ask for it, and
   * never rejects: a failed sync must leave the buyer with a stale property
   * rather than a dead checkout button.
   * @returns {Promise} Promise that resolves when the cart is up to date
   */
  sync() {
    if (this.inFlight) return this.inFlight;

    this.inFlight = this.run()
      .catch((error) => {
        console.warn('Backorder cart sync skipped:', error);
      })
      .finally(() => {
        this.inFlight = null;
      });

    return this.inFlight;
  },

  async run() {
    // Without the label there is no way to tell a backorder property from any
    // other one, and guessing would strip a buyer's real properties.
    if (!this.propertyKey) throw new Error('Backorder property label is unavailable');

    const [expectedLeadTimes, cart] = await Promise.all([this.fetchExpectedLeadTimes(), this.fetchCart()]);
    const staleLines = this.getStaleLines(cart, expectedLeadTimes);

    if (staleLines.length === 0) return;

    // Rewriting properties changes the line's key, so the writes cannot be
    // parallelised.
    for (const line of staleLines) {
      await this.updateLine(line);
    }

    document.dispatchEvent(new CustomEvent('cart:backorder-synced', { detail: { lines: staleLines } }));
  },

  /**
   * Renders sections/cart-backorder-status.liquid to get the verdict Liquid
   * would produce for each line right now.
   * @returns {Promise<Map>} Line key to lead time, empty string when not on backorder
   */
  async fetchExpectedLeadTimes() {
    const root = (window.routes && window.routes.root) || '/';
    const response = await fetch(`${root}?sections=${this.sectionId}`);

    if (!response.ok) throw new Error(`Could not render ${this.sectionId} (${response.status})`);

    const sections = await response.json();
    const markup = sections[this.sectionId];

    // The API answers 200 with a null section when rendering fails.
    if (!markup) throw new Error(`Section ${this.sectionId} did not render`);

    const dataElement = new DOMParser()
      .parseFromString(markup, 'text/html')
      .querySelector('[data-cart-backorder-status]');

    if (!dataElement) throw new Error(`Section ${this.sectionId} is missing its status data`);

    const expectedLeadTimes = new Map();

    JSON.parse(dataElement.textContent).forEach((line) => {
      expectedLeadTimes.set(line.key, this.normalize(line.lead_time));
    });

    return expectedLeadTimes;
  },

  async fetchCart() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    const response = await fetch(`${cartUrl}.js`);

    if (!response.ok) throw new Error(`Could not read the cart (${response.status})`);

    return response.json();
  },

  getStaleLines(cart, expectedLeadTimes) {
    const items = (cart && cart.items) || [];

    return items.reduce((staleLines, item) => {
      // A line the section didn't report means the cart changed between the two
      // requests, or the line has no variant. Either way, leave it alone.
      if (!expectedLeadTimes.has(item.key)) return staleLines;

      const leadTime = expectedLeadTimes.get(item.key);
      const properties = this.buildProperties(item, leadTime);

      if (this.isUpToDate(item, properties)) return staleLines;

      staleLines.push({
        key: item.key,
        quantity: item.quantity,
        leadTime: leadTime,
        properties: properties,
      });

      return staleLines;
    }, []);
  },

  /**
   * `/cart/change.js` replaces the whole properties object, so every other
   * property on the line has to be sent back alongside the corrected lead time.
   * Any locale's key is dropped first, so a line added before a language switch
   * comes back keyed in the language the buyer is reading now.
   * @param {Object} item The cart line item, as returned by /cart.js
   * @param {String} leadTime The corrected lead time, empty when not on backorder
   * @returns {Object} The full properties object to write
   */
  buildProperties(item, leadTime) {
    const properties = Object.assign({}, item.properties);
    const wasBackorder =
      this.privateKey in properties || this.knownPropertyKeys.some((key) => key in properties);

    this.knownPropertyKeys.forEach((key) => delete properties[key]);
    delete properties[this.privateKey];

    if (leadTime) {
      properties[this.propertyKey] = leadTime;
      properties[this.privateKey] = leadTime;
      return properties;
    }

    // The Ajax API ignores an empty properties object, so a line whose only
    // property was the lead time needs something to take its place. The private
    // key with a blank value serves: it stays out of checkout, and a blank lead
    // time is the truthful thing to leave on a line that no longer ships on
    // demand, so Flow and Klaviyo skip it on an is-not-empty test. Lines that
    // never carried the property are left with no properties at all, so a plain
    // line is never rewritten just to gain a marker.
    if (wasBackorder && Object.keys(properties).length === 0) {
      properties[this.privateKey] = '';
    }

    return properties;
  },

  /**
   * @param {Object} item The cart line item, as returned by /cart.js
   * @param {Object} properties The properties the line should carry
   * @returns {Boolean} Whether the line already carries exactly those properties
   */
  isUpToDate(item, properties) {
    const current = item.properties || {};
    const currentKeys = Object.keys(current);
    const nextKeys = Object.keys(properties);

    if (currentKeys.length !== nextKeys.length) return false;

    return nextKeys.every((key) => this.normalize(current[key]) === this.normalize(properties[key]));
  },

  async updateLine(line) {
    const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change.js';
    // Addressed by key: a variant id is ambiguous once the same variant sits on
    // several lines with different properties.
    const response = await fetch(changeUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        id: line.key,
        quantity: line.quantity,
        properties: line.properties,
      }),
    });

    // One line failing must not strand the rest of the cart.
    if (!response.ok) {
      console.warn(`Backorder cart sync could not update line ${line.key} (${response.status})`);
    }
  },

  normalize(value) {
    return value == null ? '' : String(value).trim();
  },

  /**
   * Defers every checkout button the theme can see until the cart is in sync.
   *
   * Listens on the capture phase so the click never reaches the button's own
   * handler: the cart page needs its form submit held back, and the Rebuy drawer
   * button runs a Vue handler the theme has no way to wrap. Delegation also
   * survives cart-footer.liquid being replaced on every AJAX cart update, and
   * Rebuy re-rendering its drawer.
   */
  bindCheckoutButtons() {
    document.addEventListener(
      'click',
      (event) => {
        if (!(event.target instanceof Element)) return;

        const button = event.target.closest(this.checkoutButtonSelector);

        if (!button || button === this.bypassButton) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        // Only the click that started the wait may re-issue one. A second click
        // on the same button, or on another checkout button in the same cart,
        // would otherwise wait on the same sync and check out twice when it
        // resolves. Locking the button is not enough on its own: this listener
        // sees the click before pointer-events can swallow it, and the Rebuy
        // drawer's button is not the one that was locked.
        if (this.pendingCheckoutButton) return;

        this.pendingCheckoutButton = button;

        // Locked rather than disabled: Rebuy binds the button's disabled
        // attribute to its own terms-accepted state.
        button.style.pointerEvents = 'none';
        button.setAttribute('aria-busy', 'true');

        this.sync().finally(() => {
          button.style.pointerEvents = '';
          button.removeAttribute('aria-busy');
          // Released before the re-issued click, so a handler that declines to
          // check out (Rebuy's unaccepted terms) leaves a working button.
          this.pendingCheckoutButton = null;

          // Re-issue the click so the cart form keeps its checkout submitter and
          // Rebuy still runs its own handler. The dispatch is synchronous, so
          // the bypass cannot outlive it.
          this.bypassButton = button;
          button.click();
          this.bypassButton = null;
        });
      },
      true
    );
  },

  /**
   * Locks the accelerated checkout buttons until the cart has been synced, since
   * a click on those cannot be deferred.
   */
  async syncBeforeWalletCheckout() {
    this.setWalletCheckoutEnabled(false);

    try {
      await this.sync();
    } finally {
      this.setWalletCheckoutEnabled(true);
    }
  },

  setWalletCheckoutEnabled(enabled) {
    document.querySelectorAll(this.walletSelector).forEach((element) => {
      element.style.pointerEvents = enabled ? '' : 'none';
    });
  },

  /**
   * The native cart drawer is rendered on every storefront page (except the
   * cart template) and includes the same accelerated checkout buttons as the
   * cart page. Those cannot be deferred, so the eager sync has to run every
   * time the drawer opens.
   *
   * Keyed off the drawer's own `is-open` class, which ModalsAndDrawers sets.
   */
  bindNativeDrawer() {
    const drawer = document.getElementById('drawer-cart');

    if (!drawer) return;

    let wasOpen = drawer.classList.contains('is-open');

    this.nativeDrawerObserver = new MutationObserver(() => {
      const isOpen = drawer.classList.contains('is-open');

      if (isOpen === wasOpen) return;

      wasOpen = isOpen;

      if (isOpen) this.syncBeforeWalletCheckout();
    });

    this.nativeDrawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    if (wasOpen) this.syncBeforeWalletCheckout();
  },

  /**
   * Rebuy renders its own drawer, including its own accelerated checkout
   * buttons, so the eager sync has to run every time that drawer opens.
   *
   * Keyed off the drawer's own `is-visible` class, which the Smart Cart template
   * in rebuy/ sets, rather than off a Rebuy event: `smartcart.ready` announces
   * initialisation on every page load, which is far too often to sync.
   */
  bindRebuyDrawer() {
    document.addEventListener('rebuy:smartcart.ready', () => this.observeRebuyDrawer());
    this.observeRebuyDrawer();
  },

  observeRebuyDrawer() {
    const drawer = document.getElementById('rebuy-cart');

    // Keyed off the element rather than off the observer, because Rebuy renders
    // its drawer under the same id: watching the node this observer is already
    // bound to is the case to skip, while a re-render leaves the old observer on
    // a detached node and the drawer the buyer opens unwatched.
    if (!drawer || drawer === this.rebuyDrawer) return;

    if (this.rebuyObserver) this.rebuyObserver.disconnect();

    this.rebuyDrawer = drawer;

    let wasVisible = drawer.classList.contains('is-visible');

    this.rebuyObserver = new MutationObserver(() => {
      const isVisible = drawer.classList.contains('is-visible');

      if (isVisible === wasVisible) return;

      wasVisible = isVisible;

      if (isVisible) this.syncBeforeWalletCheckout();
    });

    this.rebuyObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });

    if (wasVisible) this.syncBeforeWalletCheckout();
  },
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => Woolman.BackorderCartSync.init());
} else {
  Woolman.BackorderCartSync.init();
}
